// index.tsx
//
// (c) NCR Voyix Corporation 2025. All rights reserved.

import React, { useState, useEffect } from 'react'
import {
  GridCellParams,
  GridSelectionModel
} from '@mui/x-data-grid-pro'
import { connect } from 'react-redux'
import { Navigate, useNavigate } from 'react-router-dom'
import Typography from '@mui/material/Typography'
import { Stack, TextField, Button } from '@mui/material'
import { v4 } from 'uuid'
import { AppThunkDispatch, RootState } from '../../redux'
import { dateToString } from '../../utils/date'

import { getFriendlyProductName, SolutionSet } from '../../data/solutionSet'
import { isUnique } from '../../utils/solutionSet'
import ConfirmationDialog from './ConfirmationDialog'
import { buildAuthenticatedUserFromState } from '../../common/authenticatedUser'
import BreadcrumbNavigation, { BreadcrumbSegment } from '../../components/BreadcrumbNavigation'
import EditSolutionSetTable from './components/editSolutionSetTable'
import { TextBox } from '../../components/TextBox'
import { setEditableSelectedSolutionSet, setEditableSolutionSetBreadcrumbs } from '../../redux/solutionSets/slice'
import { saveSolutionSet } from '../../redux/solutionSets/thunks'

export interface SelectedProductState {
  /** The id of the product that is selected */
  id: string;
  /** The name of the product that is selected */
  name: string;
  /** The friendlyName of the product that is selected */
  friendlyName: string;
  /** The version of the product that is selected */
  version: string;
}
export interface SelectedProduct {
  /** Official state of an aloha product within a solution set; state is not expected to change unless selected product changes */
  officialState: SelectedProductState;
  /** Custom state of an aloha product within a solution set; state changes based on user selection within a solution set */
  customState: SelectedProductState;
}

function mapStateToProps(state: RootState) {
  return {
    selectedEditableSolutionSet: state.solutionSets.editableSelectedSolutionSet,
    solutionSets: state.solutionSets,
    user: state.user,
    company: state.company,
    breadcrumbs: state.solutionSets.solutionSetBreadcrumbs
  }
}
function mapDispatchToProps(dispatch: AppThunkDispatch) {
  return {
    dispatchSetEditableSelectedSolutionSet: (...args: Parameters<typeof setEditableSelectedSolutionSet>) => dispatch(setEditableSelectedSolutionSet(...args)),
    dispatchSaveSolutionSet: (...args: Parameters<typeof saveSolutionSet>) => dispatch(saveSolutionSet(...args)),
    dispatchSetEditableSolutionSetBreadcrumbs: (...args: Parameters<typeof setEditableSolutionSetBreadcrumbs>) => dispatch(setEditableSolutionSetBreadcrumbs(...args))
  }
}

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type EditSolutionSetProps = StateProps & DispatchProps;

export function EditSolutionSet({
  selectedEditableSolutionSet,
  solutionSets,
  user,
  company,
  dispatchSaveSolutionSet,
  dispatchSetEditableSelectedSolutionSet,
  breadcrumbs,
  dispatchSetEditableSolutionSetBreadcrumbs
}: EditSolutionSetProps) {
  const navigate = useNavigate()

  const [selectedProducts, setSelectedProducts] = useState<string[]>([])
  const [setName, setSetName] = useState<string>(
    selectedEditableSolutionSet ? selectedEditableSolutionSet.name : ''
  )
  const [uniqueNamePrompt, setUniqueNamePrompt] = useState(false)
  const [openDialog, setOpenDialog] = useState(false)
  const [selectedProduct, setSelectedProduct] = useState<SelectedProduct | null>(null)
  const [disabledSaveButton, setDisabledSaveButton] = useState(true)

  // ! Note: Clear the editable solution set once the user leaves the page. (Get's fired after navigate('/<route>') is invoked)
  useEffect(() => () => {
    dispatchSetEditableSelectedSolutionSet(null)
  }, [dispatchSetEditableSelectedSolutionSet])
  useEffect(() => () => {
    dispatchSetEditableSolutionSetBreadcrumbs([])
  }, [dispatchSetEditableSolutionSetBreadcrumbs])

  // * Set the breadcrumbs for the page. (Consider adding the segments to the state if we add more pages)
  const breadcrumbSegments: BreadcrumbSegment[] = [
    {
      name: 'SOLUTION SET',
      path: '/solutionsets',
      order: 0,
      testId: 'editSolutionSetBreadcrumbs'
    },
    {
      name: selectedEditableSolutionSet?.name ?? '',
      path: `/solutionsets/${selectedEditableSolutionSet?.name ?? ''}`,
      order: 1
    }
  ]

  useEffect(() => {
    // ! Use the breadcrumbNavigationHelpers function when more child pages are added and need breadcrumbs
    dispatchSetEditableSolutionSetBreadcrumbs(breadcrumbSegments)
  }, [company.selectedCompany?.id])

  useEffect(
    () => setDisabledSaveButton(setName.trim().length === 0 || selectedProducts.length === 0),
    [setName, selectedProducts.length]
  )

  const { selectedCompany } = company

  // Will force the user back to the solution sets page if this is null
  // Also will appropriately type selectedEditableSolutionSet, avoiding future
  // null checks
  if (selectedEditableSolutionSet === null) {
    return <Navigate to="/solutionsets" replace />
  }

  /** Handle Confirmation Dialog Prompt functions */
  const handleOnCancelPrompt = () => {
    setOpenDialog(false)
  }

  const handleOnConfirmPrompt = (productVersion: string) => {
    const { products } = selectedEditableSolutionSet
    if (products && selectedProduct) {
      const newProductsState = products.map((product) => ({
        ...product,
        version:
          product.name === selectedProduct.customState.name
            ? productVersion
            : product.version
      }))
      dispatchSetEditableSelectedSolutionSet({
        ...selectedEditableSolutionSet,
        products: newProductsState
      })
      setSelectedProduct({
        ...selectedProduct,
        customState: {
          ...selectedProduct.customState,
          version: productVersion
        }
      })
    }
    setOpenDialog(false)
  }

  const handleOnCellClick = (params: GridCellParams) => {
    if (params.field === '__check__') return
    const { editableSelectedSolutionSet } = solutionSets
    if (!editableSelectedSolutionSet) return

    const { products } = editableSelectedSolutionSet
    const officialProduct = products.filter(
      (p) => p.name === params.row.name
    )[0]

    setSelectedProduct({
      officialState: {
        id: officialProduct.name,
        name: officialProduct.name,
        friendlyName: getFriendlyProductName(officialProduct.name),
        version: officialProduct.version
      },
      customState: params.row
    })
  }

  const handleSelectionModelChange = (model: GridSelectionModel) => {
    // p is the row ids, id is mapped to product.name
    const { products } = selectedEditableSolutionSet
    const selectedProducts = products.filter((pkg) => model.includes(pkg.name)).map((p) => p.name)
    setSelectedProducts(selectedProducts)
  }

  const handleChangeVersionButtonClick = () => {
    setOpenDialog(true)
  }

  return (
    <>
      <Stack
        alignItems="baseline"
        paddingBottom={3}
        spacing={3}
        sx={{
          width: '600px'
        }}
      >

        <BreadcrumbNavigation breadcrumbs={breadcrumbs} />

        <Typography variant="h4">
          {`Customize Solution Set: ${selectedEditableSolutionSet?.name}`}
        </Typography>
        <Typography variant="body1">
          Rename your version of this stack, choose the solutions to include in
          this stack, and add add-ons. Some solutions cannot be excluded. Some
          solutions must be included together.
        </Typography>

        <Stack
          direction="row"
          width="100%"
        >
          <TextBox
            onChange={(value) => {
              setSetName(value)
              if (uniqueNamePrompt) setUniqueNamePrompt(false)
            }}
            label="Name"
            variant="standard"
            initialValue={setName}
            fullWidth
            error={uniqueNamePrompt}
            errorHelperText="Only able to save a solution set with a unique name"
            characterLimit={256}
          />
          <TextField
            label="Stack"
            variant="standard"
            value={selectedEditableSolutionSet?.stack ?? ''}
            sx={{
              marginLeft: 3,
              width: 'calc(20%)'
            }}
            disabled
          />
        </Stack>

      </Stack>

      <Stack width="800px">
        <Typography variant="h5">Products in Stack</Typography>
        <EditSolutionSetTable
          selectedProduct={selectedProduct}
          onCellClick={(p) => handleOnCellClick(p)}
          onGridSelectionModelChange={(model) => handleSelectionModelChange(model)}
          onCHangeVersionButtonClick={() => handleChangeVersionButtonClick()}
        />

        <Stack
          direction="row"
          width="100%"
          justifyContent="space-between"
          paddingTop={3}
        >
          <Button
            data-testid="editSolutionSetCancelButton"
            variant="outlined"
            color="primary"
            onClick={() => {
              navigate('/solutionsets')
            }}
          >
            Discard changes
          </Button>

          <Button
            data-testid="editSolutionSetSaveButton"
            variant="contained"
            color="primary"
            onClick={() => {
              const isUniqueName = isUnique(
                setName,
                solutionSets.availableSavedSolutionSets
              )

              if (isUniqueName === false) {
                setUniqueNamePrompt(true)
                return
              }

              const solutionSet: SolutionSet = {
                id: v4(),
                name: setName,
                description: selectedEditableSolutionSet?.description ?? '',
                date: dateToString(new Date()),
                products: selectedEditableSolutionSet.products.filter((p) => selectedProducts.includes(p.name)),
                type: selectedEditableSolutionSet.type,
                enhancementReleaseGuide: selectedEditableSolutionSet.enhancementReleaseGuide,
                releaseContentProfile: selectedEditableSolutionSet.releaseContentProfile,
                stack: selectedEditableSolutionSet.stack
              }
              if (user.dataCenter && company.selectedCompany?.id && user.sessionId && user.userId) {
                dispatchSaveSolutionSet(
                  {
                    solutionSet,
                    companyId: company.selectedCompany.id,
                    userId: user.userId,
                    sessionId: user.sessionId,
                    dataCenter: user.dataCenter
                  }
                )
              }
              navigate('/solutionsets')
            }}
            disabled={disabledSaveButton}
          >
            Save
          </Button>
        </Stack>
      </Stack>

      {/* Confirmation Dialog */}
      {selectedProduct && (
        <ConfirmationDialog
          open={openDialog}
          selectedProduct={selectedProduct}
          selectedCompany={selectedCompany}
          stack={selectedEditableSolutionSet.stack}
          authUser={buildAuthenticatedUserFromState(user)}
          onCancel={() => handleOnCancelPrompt()}
          onConfirm={handleOnConfirmPrompt}
        />
      )}
    </>
  )
}

export default connect(mapStateToProps, mapDispatchToProps)(EditSolutionSet)
